import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { SeoHead } from '../components/ui/SeoHead';
import { Section } from '../components/ui/Section';

export const Insights = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    // Clear any previous Soro content
    const soroBlog = document.getElementById('soro-blog');
    if (soroBlog) {
      soroBlog.innerHTML = '';
    }

    // Remove any previously injected Soro scripts
    const existingScripts = document.querySelectorAll('script[data-soro-embed]');
    existingScripts.forEach((s) => s.remove());

    // Dynamically load the Soro blog embed script
    const script = document.createElement('script');
    script.src = 'https://app.trysoro.com/api/embed/9785f04d-90bd-41d1-a71c-21d8d1bafcae';
    script.defer = true;
    script.setAttribute('data-soro-embed', 'true');

    // Append to the container to ensure it loads in the right context
    if (containerRef.current) {
      containerRef.current.appendChild(script);
    }

    return () => {
      // Cleanup on unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      // Remove any Soro-injected content
      const blog = document.getElementById('soro-blog');
      if (blog) {
        blog.innerHTML = '';
      }
    };
  }, [location.pathname]);

  return (
    <Layout>
      <SeoHead
        title="Industry Insights & Technical Articles | EnergiPro Solutions"
        description="Expert insights on well testing, pressure control, field execution, rig modifications, and oilfield operations from EnergiPro Group's engineering team."
        canonicalPath="/insights"
      />
      {/* Hero Section */}
      <div className="bg-slate-900 pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-orange-400 text-sm font-bold uppercase tracking-widest mb-3">
            Industry Knowledge & Updates
          </p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
            Insights
          </h1>
          <p className="mt-4 text-slate-300 text-lg max-w-3xl">
            Technical perspectives, operational updates, and industry intelligence from the EnergiPro team.
          </p>
        </div>
      </div>

      {/* Blog Widget Section */}
      <div className="bg-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={containerRef}>
          <div id="soro-blog" className="w-full min-h-[400px]"></div>
        </div>
      </div>

      {/* Share This Page */}
      <div className="bg-slate-50 py-10 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Share EnergiPro Insights</h3>
          <div className="flex justify-center gap-4">
            <a
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent('https://www.energiprogroup.com/insights')}&title=${encodeURIComponent('EnergiPro Group Insights')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2 bg-[#0A66C2] text-white text-sm font-bold rounded-sm hover:opacity-90 transition-opacity"
            >
              LinkedIn
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent('https://www.energiprogroup.com/insights')}&text=${encodeURIComponent('Technical insights from EnergiPro Group')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2 bg-slate-900 text-white text-sm font-bold rounded-sm hover:opacity-90 transition-opacity"
            >
              X / Twitter
            </a>
            <a
              href={`https://wa.me/?text=${encodeURIComponent('Check out EnergiPro Group insights: https://www.energiprogroup.com/insights')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2 bg-[#25D366] text-white text-sm font-bold rounded-sm hover:opacity-90 transition-opacity"
            >
              WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Related Resources */}
      <Section background="light">
        <div className="mb-10">
          <h2 className="text-3xl font-bold text-slate-900">Related Resources</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <a href="/services" className="group bg-white border border-slate-200 rounded-sm p-6 hover:shadow-md transition-shadow">
            <span className="text-xs font-bold text-orange-600 uppercase tracking-wider">Services</span>
            <h3 className="font-bold text-slate-900 mt-2 group-hover:text-orange-600 transition-colors">Operational Capabilities</h3>
            <p className="text-sm text-slate-600 mt-2">Seven integrated service lines from well testing to logistics.</p>
          </a>
          <a href="/projects" className="group bg-white border border-slate-200 rounded-sm p-6 hover:shadow-md transition-shadow">
            <span className="text-xs font-bold text-orange-600 uppercase tracking-wider">Projects</span>
            <h3 className="font-bold text-slate-900 mt-2 group-hover:text-orange-600 transition-colors">Case Studies & Execution Records</h3>
            <p className="text-sm text-slate-600 mt-2">Verified project metrics, equipment specs, and deliverables.</p>
          </a>
          <a href="/tender-support" className="group bg-white border border-slate-200 rounded-sm p-6 hover:shadow-md transition-shadow">
            <span className="text-xs font-bold text-orange-600 uppercase tracking-wider">Tender Support</span>
            <h3 className="font-bold text-slate-900 mt-2 group-hover:text-orange-600 transition-colors">Prequalification & Documentation</h3>
            <p className="text-sm text-slate-600 mt-2">Vendor registration, compliance matrices, and technical packages.</p>
          </a>
        </div>
      </Section>
    </Layout>
  );
};
